import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { transactionReportsClient } from 'clients';
import {
    PacingReport,
    RevenueByDay,
    RevenueByMonth,
    RevenueReportByRentalType,
    YoYRevenue,
} from 'types/TransactionReport';

type RevenueContextType = {
    // Reports data
    momCurrentYearReport: RevenueByMonth | null;
    momPreviousYearReport: RevenueByMonth | null;
    dodReport: RevenueByDay | null;
    revenueByRentalTypeReport: RevenueReportByRentalType | null;
    pacingReport: PacingReport | null;
    yoyRevenueReport: YoYRevenue | null;
    // Fetch functions
    getMomReport: (spotUuid: string, startDate?: string) => Promise<RevenueByMonth>;
    getMomPreviousYearReport: (spotUuid: string) => Promise<RevenueByMonth>;
    getDailyReport: (spotUuid: string, startDate: string) => Promise<RevenueByDay>;
    getRevenueByRentalTypeReport: (spotUuid: string) => Promise<RevenueReportByRentalType>;
    getPacingReport: (spotUuid: string) => Promise<PacingReport>;
    getYoyRevenueReport: (spotUuid: string, month: string) => Promise<YoYRevenue>;
};

const RevenueContext = createContext<RevenueContextType | null>(null);

type RevenueProviderProps = {
    children: ReactNode;
};

export function RevenueProvider({ children }: RevenueProviderProps): JSX.Element {
    const [momCurrentYearReport, setMomCurrentYearReport] = useState<RevenueByMonth | null>(null);
    const [momPreviousYearReport, setMomPreviousYearReport] = useState<RevenueByMonth | null>(null);
    const [dodReport, setDodReport] = useState<RevenueByDay | null>(null);
    const [revenueByRentalTypeReport, setRevenueByRentalTypeReport] = useState<RevenueReportByRentalType | null>(null);
    const [pacingReport, setPacingReport] = useState<PacingReport | null>(null);
    const [yoyRevenueReport, setYoyRevenueReport] = useState<YoYRevenue | null>(null);

    const getMomReport = useCallback(async function getMomReport(
        spotUuid: string,
        startDate?: string
    ): Promise<RevenueByMonth> {
        const response = await transactionReportsClient.getRevenueMoMReport(spotUuid, startDate);
        setMomCurrentYearReport(response);
        return response;
    },
    []);

    const getMomPreviousYearReport = useCallback(async function getMomPreviousYearReport(
        spotUuid: string
    ): Promise<RevenueByMonth> {
        const date = new Date();
        // By setting the date to next month and then date to 0 we get last day of current month
        date.setMonth(date.getMonth() + 1);
        date.setDate(0);
        // End Date should be 12 month prior current date
        date.setMonth(date.getMonth() - 12);
        const endDate = date.toISOString().split('T')[0];
        // And Start Date should be 12 month prior end date
        date.setMonth(date.getMonth() - 12);
        date.setDate(1);
        const startDate = date.toISOString().split('T')[0];
        const response = await transactionReportsClient.getRevenueMoMReport(spotUuid, startDate, endDate);
        setMomPreviousYearReport(response);
        return response;
    },
    []);

    const getDailyReport = useCallback(async function getDailyReport(
        spotUuid: string,
        startDate: string
    ): Promise<RevenueByDay> {
        const response = await transactionReportsClient.getRevenueDailyReport(spotUuid, startDate);
        setDodReport(response);
        return response;
    },
    []);

    const getRevenueByRentalTypeReport = useCallback(async function getRevenueByRentalTypeReport(
        spotUuid: string
    ): Promise<RevenueReportByRentalType> {
        const response = await transactionReportsClient.getRevenueByRentalType(spotUuid);
        setRevenueByRentalTypeReport(response);
        return response;
    },
    []);

    const getPacingReport = useCallback(async function getPacingReport(spotUuid: string): Promise<PacingReport> {
        const response = await transactionReportsClient.getPacingReport(spotUuid);
        setPacingReport(response);
        return response;
    }, []);

    const getYoyRevenueReport = useCallback(async function getYoyRevenueReport(
        spotUuid: string,
        month: string
    ): Promise<YoYRevenue> {
        const response = await transactionReportsClient.getYoYRevenueReport(spotUuid, month);
        setYoyRevenueReport(response);
        return response;
    },
    []);

    const value: RevenueContextType = {
        momCurrentYearReport,
        momPreviousYearReport,
        dodReport,
        revenueByRentalTypeReport,
        pacingReport,
        yoyRevenueReport,
        getMomReport,
        getMomPreviousYearReport,
        getDailyReport,
        getRevenueByRentalTypeReport,
        getPacingReport,
        getYoyRevenueReport,
    };

    return <RevenueContext.Provider value={value}>{children}</RevenueContext.Provider>;
}

export function useRevenueContext(): RevenueContextType {
    const context = useContext(RevenueContext);
    if (!context) {
        throw new Error('useRevenueContext must be used within a RevenueProvider');
    }
    return context;
}

export default RevenueContext;
